import flow from "lodash/fp/flow";
import negate from "lodash/fp/negate";
import join from "lodash/fp/join";
import omit from "lodash/fp/omit";
import mapValues from "lodash/fp/mapValues";
import mapKeys from "lodash/mapKeys";
import isNull from "lodash/fp/isNull";
import pickBy from "lodash/fp/pickBy";
import isUndefined from "lodash/fp/isUndefined";

const ignoreValuesFor = [
  "full-right-hand",
  "set-colors",
  "toe-alignment",
  "t-trap",
  "t-trap-reinforced",
  "pocket-lacing-type",
  "pocket-lace--glove",
  "t-spine-lacing-type",
  "t-spine-lace--glove",
  "internal-fit",
  "pad-size",
  "thigh-rise",
  "personalizedName",
  "noPersonalization",
  "center-channel",
  "knee-block-and-calf-plate",
  "top-welting",
  "bottom-welting",
  "back-cover",
  "binding",
  "elastic-and-velcro",
  "construction-lace",
  "top-spear",
  "bottom-spear",
  "calf-wrap",
  "knee-block-individual-color",
  "calf-plate-individual-color",
  "outside-zone-color",
  "outside-zone-color--glove",
  "outside-zone-color--blocker",
  "outside-bottom-zone-color",
  "outside-bottom-zone-color--glove",
  "outside-bottom-zone-color--blocker",
  "top-zone-color",
  "top-zone-color--glove",
  "top-zone-color--blocker",
  "hyp2rlite-logo-color",
  "outside-color",
  "outside-color--glove",
  "outside-color--blocker",
  "top-color",
  "top-color--glove",
  "top-color--blocker",
  "outside-bottom-color",
  "outside-bottom-color--glove",
  "outside-bottom-color--blocker",
  "inside-middle-color",
  "inside-middle-color--glove",
  "inside-middle-color--blocker",
  "inside-middle-accent-color",
  "inside-middle-accent-color--glove",
  "inside-middle-accent-color--blocker",
  "inside-bottom-color",
  "inside-bottom-color--glove",
  "inside-bottom-color--blocker",
  "spear-color",
  "spear-color--glove",
  "spear-color--blocker",
  "middle-color",
  "middle-color--glove",
  "middle-color--blocker",
  "middle-accent-color",
  "middle-accent-color--glove",
  "middle-accent-color--blocker",
  "outer-zone-color",
  "outer-zone-color--glove",
  "outer-zone-color--blocker",
  "outer-accent-color",
  "outer-accent-color--glove",
  "outer-accent-color--blocker",
  "bottom-zone-color",
  "bottom-zone-color--glove",
  "bottom-zone-color--blocker",
];

const valueOverrides = {
  "pad-construction": {
    "vapor hyperlite": "vapor-flylite",
  },
  "glove-construction": {
    "vapor hyperlite2": "vapor-flylite",
    "Vapor Hyperlite2": "vapor-flylite",

    Shadow: "fuse",
  },
  "blocker-construction": {
    "vapor hyperlite2": "vapor-flylite",
    "Vapor Hyperlite2": "vapor-flylite",
    "Vapor 2PC": "flylite-2pc",

    Shadow: "fuse",
  },
  graphic: {
    "vapor hyperlite": "vapor-flylite",
  },
};

const handleFullSetColors = (userChoiceValues) =>
  mapKeys(userChoiceValues, (value, key) =>
    key.endsWith("-color") ? key + "--pads" : key
  );

export default function getGoalieGearAddToOrderValues({
  userChoiceValues,
  product,
  materialID,
  state,
}) {
  const isSVPro = product?.productFamily === "SVProGoalieGear2026";

  const addRequestedNamingConvention = (userChoices) => ({
    ...userChoices,
    graphic:
      valueOverrides["graphic"][userChoiceValues["graphic"]] ||
      userChoiceValues["graphic"] ||
      null,
    "pad-construction":
      valueOverrides["pad-construction"][
        userChoiceValues["pad-construction"]
      ] ||
      userChoiceValues["pad-construction"] ||
      null,
    "glove-construction":
      valueOverrides["glove-construction"][
        userChoiceValues["glove-construction"]
      ] ||
      userChoiceValues["glove-construction"] ||
      null,
    "blocker-construction":
      valueOverrides["blocker-construction"][
        userChoiceValues["blocker-construction"]
      ] ||
      userChoiceValues["blocker-construction"] ||
      null,
    handedness: userChoiceValues["full-right-hand"]
      ? "full right"
      : "regular" || null,
    "toe-bridge-combined": userChoiceValues["toe-alignment"]
      ? join("-")([
          userChoiceValues["toe-bridge"],
          userChoiceValues["toe-alignment"],
        ])
      : userChoiceValues["toe-bridge"] || null,
    "pocket-t-trap": userChoiceValues["t-trap-reinforced"]
      ? join("-")([
          userChoiceValues["t-trap"],
          userChoiceValues["t-trap-reinforced"],
        ])
      : userChoiceValues["t-trap"] || null,
    "pocket-lacing": userChoiceValues["pocket-lacing-type"] || null,
    "pocket-lacing-combined":
      join("-")([
        userChoiceValues["pocket-lacing-type"],
        userChoiceValues["pocket-lace--glove"],
      ]) || null,
    "t-spine-lacing":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : userChoiceValues["t-spine-lacing-type"] || null,
    "t-spine-lacing-combined":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : join("-")([
            userChoiceValues["t-spine-lacing-type"],
            userChoiceValues["t-spine-lace--glove"],
          ]) || null,
    "hand-size": userChoiceValues["internal-fit"] || null,
    "size-value": userChoiceValues["pad-size"] || null,
    size: userChoiceValues["thigh-rise"]
      ? userChoiceValues["pad-size"] + userChoiceValues["thigh-rise"]
      : userChoiceValues["pad-size"] || null,
    "personalized-name": userChoiceValues["personalizedName"] || null,
    "knee-calf-plate-color":
      userChoiceValues["knee-block-and-calf-plate"] || null, // ProCustom/Custom only
    "knee-block-color":
      userChoiceValues["knee-block-individual-color"] || // Digi-Print
      null,
    "calf-plate-color":
      userChoiceValues["calf-plate-individual-color"] || // Digi-Print
      null,
    "no-personalization": userChoiceValues["noPersonalization"] || null,
    "center-channel-color": userChoiceValues["center-channel"] || null,
    "top-welting-color": userChoiceValues["top-welting"] || null,
    "bottom-welting-color": userChoiceValues["bottom-welting"] || null,
    "calf-wrap-color": userChoiceValues["calf-wrap"] || null,
    "back-cover-color": userChoiceValues["back-cover"] || null,
    "binding-color": userChoiceValues["binding"] || null,
    "elastic-and-velcro-color": userChoiceValues["elastic-and-velcro"] || null,
    "construction-lace-color": userChoiceValues["construction-lace"] || null,
    "top-spear-color": userChoiceValues["top-spear"] || null,
    "bottom-spear-color": userChoiceValues["bottom-spear"] || null,
    ...(isSVPro
      ? {
          "zone-1-color": userChoiceValues["top-zone-color"] || null,
          "zone-2-color": userChoiceValues["middle-color"] || null,
          "zone-3-color": userChoiceValues["middle-accent-color"] || null,
          "zone-4-color": userChoiceValues["outer-zone-color"] || null,
          "zone-5-color": userChoiceValues["outer-accent-color"] || null,
          "zone-6-color": userChoiceValues["bottom-zone-color"] || null,
        }
      : {
          "zone-1-color":
            userChoiceValues["outside-zone-color"] ||
            userChoiceValues["zone-1-color"] ||
            null,
          "zone-2-color":
            userChoiceValues["top-zone-color"] ||
            userChoiceValues["zone-1-color"] ||
            null,
          "zone-3-color":
            userChoiceValues["outside-bottom-zone-color"] ||
            userChoiceValues["zone-1-color"] ||
            null,
          "zone-4-color":
            userChoiceValues["inside-middle-color"] ||
            userChoiceValues["zone-1-color"] ||
            null,
          "zone-5-color":
            userChoiceValues["inside-middle-accent-color"] ||
            userChoiceValues["zone-1-color"] ||
            null,
          "zone-6-color":
            userChoiceValues["inside-bottom-color"] ||
            userChoiceValues["zone-1-color"] ||
            null,
          "zone-7-color":
            userChoiceValues["spear-color"] ||
            userChoiceValues["zone-1-color"] ||
            null,
        }),
    "flylite-logo-color": userChoiceValues["hyp2rlite-logo-color"] || null,
  });

  const svProZoneOverrides = isSVPro && {
    // Glove
    "zone-1-color--glove":
      userChoiceValues["top-zone-color--glove"] ||
      userChoiceValues["top-zone-color"] ||
      null,
    "zone-2-color--glove":
      userChoiceValues["middle-color--glove"] ||
      userChoiceValues["middle-color"] ||
      null,
    "zone-3-color--glove":
      userChoiceValues["middle-accent-color--glove"] ||
      userChoiceValues["middle-accent-color"] ||
      null,
    "zone-4-color--glove":
      userChoiceValues["outer-zone-color--glove"] ||
      userChoiceValues["outer-zone-color"] ||
      null,
    "zone-5-color--glove":
      userChoiceValues["outer-accent-color--glove"] ||
      userChoiceValues["outer-accent-color"] ||
      null,
    "zone-6-color--glove":
      userChoiceValues["bottom-zone-color--glove"] ||
      userChoiceValues["bottom-zone-color"] ||
      null,

    // Blocker
    "zone-1-color--blocker":
      userChoiceValues["top-zone-color--blocker"] ||
      userChoiceValues["top-zone-color"] ||
      null,
    "zone-2-color--blocker":
      userChoiceValues["middle-color--blocker"] ||
      userChoiceValues["middle-color"] ||
      null,
    "zone-3-color--blocker":
      userChoiceValues["middle-accent-color--blocker"] ||
      userChoiceValues["middle-accent-color"] ||
      null,
    "zone-4-color--blocker":
      userChoiceValues["outer-zone-color--blocker"] ||
      userChoiceValues["outer-zone-color"] ||
      null,
    "zone-5-color--blocker":
      userChoiceValues["outer-accent-color--blocker"] ||
      userChoiceValues["outer-accent-color"] ||
      null,
    "zone-6-color--blocker":
      userChoiceValues["bottom-zone-color--blocker"] ||
      userChoiceValues["bottom-zone-color"] ||
      null,
  };

  const gloveAndBlockerZoneColors = {
    // Glove zone colors
    "zone-1-color--glove":
      userChoiceValues["zone-1-color--glove"] ||
      userChoiceValues["zone-1-color"] ||
      userChoiceValues["outside-zone-color--glove"] ||
      userChoiceValues["outside-zone-color"] ||
      null,
    "zone-2-color--glove":
      userChoiceValues["zone-2-color--glove"] ||
      userChoiceValues["zone-2-color"] ||
      userChoiceValues["top-zone-color--glove"] ||
      userChoiceValues["top-zone-color"] ||
      null,
    "zone-3-color--glove":
      userChoiceValues["zone-3-color--glove"] ||
      userChoiceValues["zone-3-color"] ||
      userChoiceValues["outside-bottom-color--glove"] ||
      userChoiceValues["outside-bottom-zone-color--glove"] ||
      userChoiceValues["outside-bottom-zone-color"] ||
      userChoiceValues["outside-bottom-color"] ||
      null,
    "zone-4-color--glove":
      userChoiceValues["zone-4-color--glove"] ||
      userChoiceValues["zone-4-color"] ||
      userChoiceValues["inside-middle-color--glove"] ||
      userChoiceValues["inside-middle-color"] ||
      null,
    "zone-5-color--glove":
      userChoiceValues["zone-5-color--glove"] ||
      userChoiceValues["zone-5-color"] ||
      userChoiceValues["inside-middle-accent-color--glove"] ||
      userChoiceValues["inside-middle-accent-color"] ||
      null,
    "zone-6-color--glove":
      userChoiceValues["zone-6-color--glove"] ||
      userChoiceValues["zone-6-color"] ||
      userChoiceValues["inside-bottom-color--glove"] ||
      userChoiceValues["inside-bottom-color"] ||
      null,
    "zone-7-color--glove":
      userChoiceValues["zone-7-color--glove"] ||
      userChoiceValues["zone-7-color"] ||
      userChoiceValues["spear-color--glove"] ||
      userChoiceValues["spear-color"] ||
      null,
    "zone-8-color--glove":
      userChoiceValues["zone-8-color--glove"] ||
      userChoiceValues["zone-8-color"] ||
      null,
    "zone-9-color--glove":
      userChoiceValues["zone-9-color--glove"] ||
      userChoiceValues["zone-9-color"] ||
      null,

    // Blocker zone colors
    "zone-1-color--blocker":
      userChoiceValues["zone-1-color--blocker"] ||
      userChoiceValues["zone-1-color"] ||
      userChoiceValues["outside-zone-color--blocker"] ||
      userChoiceValues["outside-zone-color"] ||
      null,
    "zone-2-color--blocker":
      userChoiceValues["zone-2-color--blocker"] ||
      userChoiceValues["zone-2-color"] ||
      userChoiceValues["top-zone-color--blocker"] ||
      userChoiceValues["top-zone-color"] ||
      null,
    "zone-3-color--blocker":
      userChoiceValues["zone-3-color--blocker"] ||
      userChoiceValues["zone-3-color"] ||
      userChoiceValues["outside-bottom-color--blocker"] ||
      userChoiceValues["outside-bottom-zone-color--blocker"] ||
      userChoiceValues["outside-bottom-zone-color"] ||
      userChoiceValues["outside-bottom-color"] ||
      null,
    "zone-4-color--blocker":
      userChoiceValues["zone-4-color--blocker"] ||
      userChoiceValues["zone-4-color"] ||
      userChoiceValues["inside-middle-color--blocker"] ||
      userChoiceValues["inside-middle-color"] ||
      null,
    "zone-5-color--blocker":
      userChoiceValues["zone-5-color--blocker"] ||
      userChoiceValues["zone-5-color"] ||
      userChoiceValues["inside-middle-accent-color--blocker"] ||
      userChoiceValues["inside-middle-accent-color"] ||
      null,
    "zone-6-color--blocker":
      userChoiceValues["zone-6-color--blocker"] ||
      userChoiceValues["zone-6-color"] ||
      userChoiceValues["inside-bottom-color--blocker"] ||
      userChoiceValues["inside-bottom-color"] ||
      null,
    "zone-7-color--blocker":
      userChoiceValues["zone-7-color--blocker"] ||
      userChoiceValues["zone-7-color"] ||
      userChoiceValues["spear-color--blocker"] ||
      userChoiceValues["spear-color"] ||
      null,
    "zone-8-color--blocker":
      userChoiceValues["zone-8-color--blocker"] ||
      userChoiceValues["zone-8-color"] ||
      null,
    "zone-9-color--blocker":
      userChoiceValues["zone-9-color--blocker"] ||
      userChoiceValues["zone-9-color"] ||
      null,
  };

  const gloveAndBlockerNonZoneColors = {
    "bauer-logo-color--glove":
      userChoiceValues["bauer-logo-color--glove"] ||
      userChoiceValues["bauer-logo-color"] ||
      null,
    "flylite-logo-color--glove":
      userChoiceValues["hyp2rlite-logo-color--glove"] ||
      userChoiceValues["hyp2rlite-logo-color"] ||
      null,
    "supreme-logo-color--glove":
      userChoiceValues["supreme-logo-color--glove"] ||
      userChoiceValues["supreme-logo-color"] ||
      null,
    "supreme-model-logo-color--glove":
      userChoiceValues["supreme-model-logo-color--glove"] ||
      userChoiceValues["supreme-model-logo-color"] ||
      null,

    "binding-color--glove":
      userChoiceValues["binding--glove"] || userChoiceValues["binding"] || null,
    "elastic-and-velcro-color--glove":
      userChoiceValues["elastic-and-velcro--glove"] ||
      userChoiceValues["elastic-and-velcro"] ||
      null,
    "construction-lace-color--glove":
      userChoiceValues["construction-lace--glove"] ||
      userChoiceValues["construction-lace"] ||
      null,

    "pocket-lace-color--glove": userChoiceValues["pocket-lace--glove"] || null,

    "t-spine-lace-color--glove":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : userChoiceValues["t-spine-lace--glove"] || null,

    "bauer-logo-color--blocker":
      userChoiceValues["bauer-logo-color--blocker"] ||
      userChoiceValues["bauer-logo-color"] ||
      null,
    "flylite-logo-color--blocker":
      userChoiceValues["hyp2rlite-logo-color--blocker"] ||
      userChoiceValues["hyp2rlite-logo-color"] ||
      null,
    "supreme-logo-color--blocker":
      userChoiceValues["supreme-logo-color--blocker"] ||
      userChoiceValues["supreme-logo-color"] ||
      null,

    "supreme-model-logo-color--blocker":
      userChoiceValues["supreme-model-logo-color--blocker"] ||
      userChoiceValues["supreme-model-logo-color"] ||
      null,

    "binding-color--blocker":
      userChoiceValues["binding--blocker"] ||
      userChoiceValues["binding"] ||
      null,
    "elastic-and-velcro-color--blocker":
      userChoiceValues["elastic-and-velcro--blocker"] ||
      userChoiceValues["elastic-and-velcro"] ||
      null,
    "construction-lace-color--blocker":
      userChoiceValues["construction-lace--blocker"] ||
      userChoiceValues["construction-lace"] ||
      null,
  };

  const digiRetroDesignOverrides = () => {
    if (userChoiceValues["retro-design"] === "reactor-4-chicago") {
      return {
        "center-channel-color--pads": "red",
        "elastic-and-velcro-color--pads": "red",
        "binding-color--pads": "black",
        "calf-wrap-color--pads": "black",

        "elastic-and-velcro-color--glove": "red",
        "binding-color--glove": "black",
        "catcher-back-cover-accent--glove": "red",
        "t-trap-color--glove": "red",

        "elastic-and-velcro-color--blocker": "red",
        "binding-color--blocker": "black",
        "blocker-back-cover-accent--blocker": "red",
      };
    }
    if (userChoiceValues["retro-design"] === "reactor-4-detroit") {
      return {
        "center-channel-color--pads": "red",
        "elastic-and-velcro-color--pads": "red",

        "elastic-and-velcro-color--glove": "red",
        "catcher-back-cover-accent--glove": "red",

        "elastic-and-velcro-color--blocker": "red",
        "blocker-back-cover-accent--blocker": "red",
      };
    }
    if (userChoiceValues["retro-design"] === "reactor-4-los-angeles") {
      return {
        "center-channel-color--pads": "black",
        "elastic-and-velcro-color--pads": "black",

        "elastic-and-velcro-color--glove": "black",
        "catcher-back-cover-accent--glove": "black",

        "elastic-and-velcro-color--blocker": "black",
        "blocker-back-cover-accent--blocker": "black",
      };
    }
    if (userChoiceValues["retro-design"] === "reactor-4-Toronto") {
      return {
        "center-channel-color--pads": "blue",
        "elastic-and-velcro-color--pads": "blue",

        "elastic-and-velcro-color--glove": "blue",
        "catcher-back-cover-accent--glove": "blue",

        "elastic-and-velcro-color--blocker": "blue",
        "blocker-back-cover-accent--blocker": "blue",
      };
    } else return {};
  };

  const zoneColors = isSVPro ? svProZoneOverrides : gloveAndBlockerZoneColors;

  const handleGloveAndBlockerColors = flow(
    (obj) => ({
      ...obj,
      ...pickBy(negate(isNull))(zoneColors),
      ...gloveAndBlockerNonZoneColors,
    }),
    pickBy(negate(isNull))
  );

  const overrideValuesForDigiRetro = flow((obj) => ({
    ...obj,
    ...digiRetroDesignOverrides(),
  }));

  return flow(
    pickBy(negate(isUndefined)),
    mapValues((val) => val.toString().toLowerCase()),
    omit(ignoreValuesFor),
    addRequestedNamingConvention,
    pickBy(negate(isNull)),
    // Rename all full set colors by appending '--pads'
    handleFullSetColors,
    // Add '--glove' and '--blocker' color values, even if same as full set
    handleGloveAndBlockerColors,

    // Override values for specific retro designs
    overrideValuesForDigiRetro,

    // Remove brackets and contents AND kebab-case
    mapValues((val) =>
      val
        .toString()
        .replace(/ *\([^)]*\) */g, "")
        .replace(/[\s-]+/g, "-")
    ),
    mapValues((val) => val.toLowerCase()),
    (userChoices) => ({
      ...userChoices,
      "material-id": materialID,
      ProductFamily: product?.productFamily,
      product: state?.app?.formData?.product,
      // Overwrite personalized name here to ignore "remove brackets and kebab-case"
      ...(userChoiceValues?.personalizedName
        ? {
            "personalized-name":
              userChoiceValues?.personalizedName.toLowerCase(),
          }
        : {}),
    })
  )(userChoiceValues);
}
